import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import {
  ListenSpalte,
  ListenStruktur,
  SPALTEN_DATENTYPEN,
  SpaltenDatentyp,
  istReservierterSpaltenname,
  pruefeStruktur,
  strukturSpalten
} from '../../../../../Models/messstelle.model';


/** Eine Spalte im Editor – mit lokalem Schlüssel für Drag & Drop und trackBy. */
interface EditorSpalte extends ListenSpalte {
  key: string;
}


/**
 * Spalten einer manuell erstellten Liste bzw. Vorlage definieren.
 *
 * Je Spalte: Name, Datentyp, Pflicht, Reihenfolge (Ziehen oder Pfeile) und
 * die Auswahl der Kennzeichnungsspalte. Die Prüfung entspricht der des
 * Servers (eindeutige Namen, keine internen Felder, genau eine Kennzeichnung,
 * Kennzeichnung ist immer Pflicht) – `gueltigChange` meldet, ob gespeichert
 * werden darf.
 *
 * Der Editor arbeitet auf einer eigenen Kopie und meldet jede Änderung über
 * `strukturChange`. Stabile Spalten-Ids bleiben erhalten; neue Spalten gehen
 * ohne Id hinaus, der Server vergibt sie.
 */
@Component({
  selector: 'app-struktur-editor',
  templateUrl: './struktur-editor.component.html',
  styleUrls: ['./struktur-editor.component.css']
})
export class StrukturEditorComponent implements OnChanges {

  /** Ausgangsstruktur. Null oder leer = neu beginnen. */
  @Input() struktur: ListenStruktur | null | undefined;

  @Input() disabled = false;

  /**
   * Spalten-Ids, die beim Entfernen einen Hinweis bekommen, weil Messstellen
   * Werte darin haben. Nur beim Bearbeiten einer bestehenden Liste belegt.
   */
  @Input() spaltenMitWerten: { [spaltenId: string]: number } = {};

  @Output() strukturChange = new EventEmitter<ListenStruktur>();
  @Output() gueltigChange = new EventEmitter<boolean>();

  spalten: EditorSpalte[] = [];

  readonly datentypen = SPALTEN_DATENTYPEN;

  /** Fehlermeldungen über die ganze Struktur, wie sie auch der Server melden würde. */
  fehler: string[] = [];

  private naechsterKey = 1;

  private zuletztGemeldeteGueltigkeit: boolean | null = null;

  private zuletztGesendeteStruktur: ListenStruktur | null = null;


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['struktur']) {
      const change = changes['struktur'];

      // Beim ersten Laden die Struktur übernehmen.
      if (change.firstChange) {
        this.uebernehme(change.currentValue);
        return;
      }

      // Wenn die Änderung von diesem Editor selbst stammt,
      // darf die Spaltenliste nicht neu aufgebaut werden.
      if (change.currentValue === this.zuletztGesendeteStruktur) {
        return;
      }

      // Externe Änderungen, z. B. eine andere Vorlage laden.
      this.uebernehme(change.currentValue);
    }
  }


  private uebernehme(struktur: ListenStruktur | null | undefined): void {
    this.spalten = strukturSpalten(struktur).map(s => ({
      ...s,
      name: s.name ?? '',
      datentyp: s.datentyp ?? 'Text',
      pflicht: !!s.pflicht || !!s.istKennzeichnung,
      istKennzeichnung: !!s.istKennzeichnung,
      key: s.id || `neu-${this.naechsterKey++}`
    }));

    this.pruefe(false);
  }


  // ── Bearbeiten ──

  spalteHinzufuegen(name = '', datentyp: SpaltenDatentyp = 'Text'): void {
    if (this.disabled) {
      return;
    }

    const istErste = this.spalten.length === 0;

    this.spalten.push({
      id: '',
      key: `neu-${this.naechsterKey++}`,
      name,
      datentyp,
      // Die erste Spalte ist meist die Kennzeichnung – ein Vorschlag, kein Zwang.
      pflicht: istErste,
      reihenfolge: this.spalten.length,
      istKennzeichnung: istErste
    });

    this.melde();
  }


  /** Schnellstart für leere Strukturen: KKS, Langtext, Medium, Signal. */
  beispielEinfuegen(): void {
    if (this.disabled || this.spalten.length > 0) {
      return;
    }

    this.spalteHinzufuegen('KKS');
    this.spalteHinzufuegen('Langtext');
    this.spalteHinzufuegen('Medium');
    this.spalteHinzufuegen('Signal');
  }


  spalteEntfernen(index: number): void {
    if (this.disabled) {
      return;
    }

    this.spalten.splice(index, 1);
    this.melde();
  }


  kennzeichnungWaehlen(key: string): void {
    if (this.disabled) {
      return;
    }

    for (const spalte of this.spalten) {
      spalte.istKennzeichnung = spalte.key === key;

      if (spalte.istKennzeichnung) {
        spalte.pflicht = true;
      }
    }

    this.melde();
  }


  get kennzeichnungKey(): string | null {
    return this.spalten.find(s => s.istKennzeichnung)?.key ?? null;
  }


  verschieben(index: number, richtung: -1 | 1): void {
    const ziel = index + richtung;

    if (this.disabled || ziel < 0 || ziel >= this.spalten.length) {
      return;
    }

    moveItemInArray(this.spalten, index, ziel);
    this.melde();
  }


  abgelegt(event: CdkDragDrop<EditorSpalte[]>): void {
    if (this.disabled || event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(this.spalten, event.previousIndex, event.currentIndex);
    this.melde();
  }


  /** Nach jeder Eingabe – Name, Typ, Pflicht. */
  geaendert(): void {
    this.melde();
  }


  // ── Prüfung je Zeile ──

  /** Fehler, der an genau dieser Zeile steht; leer, wenn die Zeile in Ordnung ist. */
  zeilenFehler(spalte: EditorSpalte): string {
    const name = (spalte.name ?? '').trim();

    if (!name) {
      return 'Name fehlt';
    }

    if (istReservierterSpaltenname(name)) {
      return 'Internes Feld – bitte anders benennen';
    }

    const doppelt = this.spalten.some(s =>
      s !== spalte && (s.name ?? '').trim().toLowerCase() === name.toLowerCase());

    return doppelt ? 'Name bereits vergeben' : '';
  }


  anzahlWerte(spalte: EditorSpalte): number {
    return spalte.id ? (this.spaltenMitWerten[spalte.id] ?? 0) : 0;
  }


  trackByKey(_index: number, spalte: EditorSpalte): string {
    return spalte.key;
  }


  // ── Ausgabe ──

  private melde(): void {
    this.spalten.forEach((s, i) => s.reihenfolge = i);

    this.pruefe(true);

    const struktur = this.aktuelleStruktur();

    // Referenz merken, damit ngOnChanges die eigenen
    // Änderungen nicht erneut als externe Eingabe übernimmt.
    this.zuletztGesendeteStruktur = struktur;

    this.strukturChange.emit(struktur);
  }


  private pruefe(emit: boolean): void {
    this.fehler = pruefeStruktur(this.aktuelleStruktur());

    const gueltig = this.fehler.length === 0;

    if (emit || this.zuletztGemeldeteGueltigkeit !== gueltig) {
      this.zuletztGemeldeteGueltigkeit = gueltig;
      this.gueltigChange.emit(gueltig);
    }
  }


  /** Die Struktur, wie sie zum Server geht: ohne lokale Schlüssel, Namen getrimmt. */
  aktuelleStruktur(): ListenStruktur {
    return {
      spalten: this.spalten.map((s, i) => ({
        id: s.id,
        name: (s.name ?? '').trim(),
        datentyp: s.datentyp,
        pflicht: s.istKennzeichnung || !!s.pflicht,
        reihenfolge: i,
        istKennzeichnung: !!s.istKennzeichnung
      }))
    };
  }
}
